import Papa from 'papaparse';
import * as XLSX from 'xlsx';

export interface GraphMonitoringUploadParseResult {
  fileName: string;
  orgNumbers: string[];
  previewOrgNumbers: string[];
  duplicateCount: number;
  invalidRowCount: number;
  totalRowCount: number;
}

function getFileExtension(fileName: string) {
  const normalized = fileName.toLowerCase();

  if (normalized.endsWith('.csv')) {
    return 'csv';
  }

  if (normalized.endsWith('.xlsx') || normalized.endsWith('.xls')) {
    return 'excel';
  }

  return null;
}

function normalizeOrgNumberCandidate(value: unknown) {
  if (value === null || value === undefined) {
    return null;
  }

  const digits = String(value).replace(/\D/g, '');

  if (digits.length === 10) {
    return digits;
  }

  if (digits.length === 12) {
    return digits.slice(-10);
  }

  return null;
}

function extractOrgNumberFromRow(row: unknown[]) {
  for (const cell of row) {
    const orgNumber = normalizeOrgNumberCandidate(cell);

    if (orgNumber) {
      return orgNumber;
    }
  }

  return null;
}

function isNonEmptyRow(row: unknown[]) {
  return row.some((cell) => String(cell ?? '').trim() !== '');
}

function isLikelyHeaderRow(row: unknown[]) {
  return (
    !extractOrgNumberFromRow(row) &&
    row.some((cell) => /[A-Za-z\u00C0-\u017F]/.test(String(cell ?? '')))
  );
}

function parseCsvFile(file: File) {
  return new Promise<unknown[][]>((resolve, reject) => {
    Papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      complete: (results: { data: unknown[][] }) => resolve(results.data),
      error: () => reject(new Error('Failed to parse the CSV file.')),
    });
  });
}

function parseExcelFile(file: File) {
  return new Promise<unknown[][]>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
          defval: '',
        }) as unknown[][];

        resolve(rows);
      } catch {
        reject(new Error('Failed to parse the Excel file.'));
      }
    };

    reader.onerror = () => reject(new Error('Failed to read the uploaded file.'));
    reader.readAsArrayBuffer(file);
  });
}

export async function parseGraphMonitoringUploadFile(
  file: File,
): Promise<GraphMonitoringUploadParseResult> {
  const extension = getFileExtension(file.name);

  if (!extension) {
    throw new Error('Upload a CSV or Excel file.');
  }

  const rawRows =
    extension === 'csv' ? await parseCsvFile(file) : await parseExcelFile(file);
  const populatedRows = rawRows.filter(isNonEmptyRow);
  const rows =
    populatedRows.length > 0 && isLikelyHeaderRow(populatedRows[0])
      ? populatedRows.slice(1)
      : populatedRows;

  const uniqueOrgNumbers = new Set<string>();
  let duplicateCount = 0;
  let invalidRowCount = 0;

  rows.forEach((row) => {
    const orgNumber = extractOrgNumberFromRow(row);

    if (!orgNumber) {
      invalidRowCount += 1;
      return;
    }

    if (uniqueOrgNumbers.has(orgNumber)) {
      duplicateCount += 1;
      return;
    }

    uniqueOrgNumbers.add(orgNumber);
  });

  const orgNumbers = Array.from(uniqueOrgNumbers);

  return {
    fileName: file.name,
    orgNumbers,
    previewOrgNumbers: orgNumbers.slice(0, 8),
    duplicateCount,
    invalidRowCount,
    totalRowCount: rows.length,
  };
}
